/*eslint-disable*/
import cloneDeep from 'lodash/cloneDeep';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import MaskedInput from 'react-text-mask';

// @mui/material components

import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { makeStyles } from '@mui/styles';
import { toast } from 'react-toastify';

// material ui icons
import Check from '@mui/icons-material/Check';
import Edit from '@mui/icons-material/Edit';
import UserIcon from '@mui/icons-material/Person';
import Environment from '@mui/icons-material/SettingsInputComponent';

// core components
import CustomInput from 'components/CustomInput/CustomInput.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';

import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Button from 'components/CustomButtons/Button.js';
import Table from 'components/Table/Table.js';

import RoleSelector from './RoleSelector';

// style for this view
import styles from 'assets/jss/material-dashboard-pro-react/views/userEditStyle.js';

// Redux
import { useGetTenantClientQuery, useLazyGetClientTenantsQuery } from 'api/client.api';
import uploadImageFile from 'api/imageAPI';
import { useAvailableLanguages } from 'hooks/useAvailableLanguages';
import { useGetBaseRolesQuery } from 'api/role.api';
import {
  useCreateUserMutation,
  useGetClientUsersQuery,
  useReinviteUserMutation,
  useUpdateUserMutation,
} from 'api/user.api';
import { selectViewIsDirty, setViewIsDirty } from 'features/ux/uxSlice';
import { isAbortError, validateUserEmail } from '../../utils';
import { emailMask } from '../../utils/customMaskEmail';

const useStyles = makeStyles(styles);

const emailMaskProps = {
  guide: false,
  placeholderChar: '\u2000',
  keepCharPositions: true,
  mask: emailMask,
};

const emailMaskCustom = (props) => {
  let { inputRef, ...other } = props;

  const handleCursorPosition = (event) => {
    const { value } = event.target;
    if (!value.includes('@')) {
      const newValue = value + '@';
      event.target.value = newValue;
      const position = newValue.indexOf('@');
      event.target.setSelectionRange(position, position);
    }
    props?.onChange(event);
  };

  if (typeof inputRef !== 'function') {
    inputRef = () => {};
  }

  return (
    <MaskedInput
      {...other}
      {...emailMaskProps}
      keepCharPositions={true}
      onChange={handleCursorPosition}
      ref={(ref) => {
        inputRef(ref ? ref.inputElement : null);
      }}
    />
  );
};

export default function User(props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const user = location.state.user;
  const newUser = location.state.new;
  const { data: client } = useGetTenantClientQuery();
  const [getClientTenants] = useLazyGetClientTenantsQuery();
  const classes = useStyles();
  const supportedLanguages = useAvailableLanguages();
  const { data: baseRoles } = useGetBaseRolesQuery();
  const viewIsDirty = useSelector(selectViewIsDirty);
  const ntRoles = baseRoles.filter((role) => role.scope === 'NT');
  const { data: clientUsers = [] } = useGetClientUsersQuery(client?._id, {
    skip: !client?._id,
  });

  const [fullName, setFullName] = useState(user.full_name);
  const [fullNameState, setFullNameState] = useState('');
  const [fullNameError, setFullNameError] = useState('');
  const [displayName, setDisplayName] = useState(user.display_name);
  const [displayNameState, setDisplayNameState] = useState('');
  const [displayNameError, setDisplayNameError] = useState('');
  const [emailAddress, setEmailAddress] = useState(user.email_address);
  const [emailAddressState, setEmailAddressState] = useState('');
  const [emailAddressError, setEmailAddressError] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(user.phone_number);
  const [preferredLanguage, setPreferredLanguage] = useState(
    user.preferred_language,
  );
  const [preferredLanguageState, setPreferredLanguageState] = useState('');
  const [preferredLanguageError, setPreferredLanguageError] = useState('');
  const [status, setStatus] = useState(user.status);
  const [statusState, setStatusState] = useState('');
  const [statusError, setStatusError] = useState('');
  const [defaultEnvironment, setDefaultEnvironment] = useState(
    user.default_environment,
  );
  const [defaultEnvironmentState, setDefaultEnvironmentState] = useState('');
  const [defaultEnvironmentError, setDefaultEnvironmentError] = useState('');
  const [administratorRole, setAdministratorRole] = useState(user.nt_role);
  const [administratorRoleState, setAdministratorRoleState] = useState('');
  const [administratorRoleError, setAdministratorRoleError] = useState('');
  const [error, setError] = useState(false);

  const [createUser] = useCreateUserMutation();
  const [reinviteManagedUser] = useReinviteUserMutation();
  const [updateManagedUser] = useUpdateUserMutation();

  const [dirtyData, setDirtyData] = useState(false);

  // Redirect target queued by a successful save, deferred to an effect that
  // fires once `viewIsDirty` flips false so RouterPrompt can lift its
  // history.block before we navigate. Component-scoped so it can only be read by
  // the instance that set it.
  const pendingRedirectRef = useRef(null);

  // The email the most recent save was submitted with. A new user has no `_id`
  // and an empty `email_address`, so its just-created record reappearing in
  // `clientUsers` matches neither exclusion in validateUserEmail and is flagged
  // as a duplicate of itself (MIST-5468); treating the submitted email as the
  // user's own restores that self-exclusion.
  const submittedEmailRef = useRef('');

  // function that returns true if value is email, false otherwise
  const verifyEmail = (value, original) => {
    const { valid, error } = validateUserEmail(
      value,
      submittedEmailRef.current || user.email_address,
      clientUsers,
      user._id,
    );

    setEmailAddressError(valid ? '' : error);

    if (valid && value?.trim() !== original) dispatch(setViewIsDirty(true));
    return valid;
  };

  // function that verifies if a string has a given length or not
  const verifyLength = (value, original, length, required = true) => {
    if (value && value.length >= length) {
      if (value !== original) dispatch(setViewIsDirty(true));
      return true;
    } else if (!required && (!value || value.length === 0)) {
      if (value !== original) dispatch(setViewIsDirty(true));
      return true;
    }
    return false;
  };

  // verify the default environment is refers to an environment where the user has an assigned role
  const verifyDefaultEnvironment = () => {
    var valid = true;
    if (!user.nt_administrator) {
      const environment = tenantRoles.find(
        (tenant) => tenant.tenant_id === defaultEnvironment,
      );
      valid = environment ? environment.role !== 'Unauthorized' : false;
      setDefaultEnvironmentState(valid ? '' : 'error');
    }
    return valid;
  };

  const getInitialRoles = async () => {
    let client_id = undefined;
    if (client && client._id) {
      client_id = client._id;
    } else if (user) {
      client_id = user.client_id;
    } else {
      return;
    }
    let clientTenants = await getClientTenants(client_id).unwrap();
    const environmentList = await clientTenants?.map(async (tenant, key) => {
      // determine if the user already has a role for this tenant
      let result = await user.roles.find(
        (tr) => tr.tenant_id === tenant.tenant_id,
      );
      // use the existing role if defined, otherwise indicate the user is unauthorized
      let tenantRole = result ? result.role : 'Unauthorized';

      return {
        tenant_id: tenant.tenant_id,
        label: tenant.tenant_label,
        role: tenantRole,
      };
    });
    return Promise.all(environmentList);
  };

  const [tenantRoles, setTenantRoles] = useState([]);

  const initializeRoles = async () => {
    let initialRoles = await getInitialRoles();
    setTenantRoles(initialRoles);
  };

  useEffect(() => {
    initializeRoles();
  }, [client]);

  const handleUpdateManagedUser = async (user) => {
    if (user.avatar && typeof user.avatar.name == 'string') {
      user.avatar = await uploadImageFile(user.client_id, user.avatar);
    }

    await updateManagedUser(user).unwrap();
  };

  const reinviteUser = async () => {
    await reinviteManagedUser(user).unwrap();
    if (!dirtyData) props.history.push('/admin/users');
  };

  const deactivateUser = async () => {
    var updatedUser = cloneDeep(user);
    updatedUser.status = 'inactive';
    await handleUpdateManagedUser(updatedUser);
    if (!dirtyData) props.history.push('/admin/users');
  };

  useEffect(() => {
    if (!viewIsDirty) {
      return;
    }
    let error = false;
    if (!verifyLength(fullName, fullName, 3)) {
      error = true;
      setFullNameState('error');
    }
    if (!verifyLength(displayName, displayName, 3)) {
      error = true;
      setDisplayNameState('error');
    }
    if (!verifyEmail(emailAddress, emailAddress)) {
      error = true;
      setEmailAddressState('error');
    }
    if (!verifyDefaultEnvironment()) {
      error = true;
      setDefaultEnvironmentState('error');
    }
    if (!user.nt_administrator && defaultEnvironment === '') {
      error = true;
      setDefaultEnvironmentState('error');
    }
    if (user.nt_administrator && administratorRole === '') {
      error = true;
      setAdministratorRoleState('error');
    }
    setError(error);
  }, [
    fullName,
    displayName,
    emailAddress,
    phoneNumber,
    defaultEnvironment,
    administratorRole,
    viewIsDirty,
    tenantRoles,
    clientUsers,
  ]);

  const changeClick = async () => {
    const updatedUser = cloneDeep(user);

    if (error === false) {
      updatedUser.full_name = fullName;
      updatedUser.display_name = displayName;
      updatedUser.email_address = emailAddress;
      updatedUser.status = status;
      updatedUser.preferred_language = preferredLanguage;
      updatedUser.default_environment = defaultEnvironment;
      updatedUser.nt_role = administratorRole;

      if (phoneNumber) {
        updatedUser.phone_number = phoneNumber;
      } else {
        delete updatedUser.phone_number;
      }

      const updatedRoles = [];
      tenantRoles.forEach((tenant) => {
        if (tenant.role !== 'Unauthorized')
          updatedRoles.push({ tenant_id: tenant.tenant_id, role: tenant.role });
      });
      updatedUser.roles = updatedRoles;

      // Remember the email we're saving so the account isn't flagged as a
      // duplicate of itself after the backend record reappears in clientUsers.
      submittedEmailRef.current = updatedUser.email_address;

      try {
        if (newUser) await createUser(updatedUser).unwrap();
        else await handleUpdateManagedUser(updatedUser);

        pendingRedirectRef.current = '/admin/users';
        dispatch(setViewIsDirty(false));
        setError(false);
      } catch (err) {
        // An aborted request — the client-side timeout elapsing while the POST
        // is still in flight, which Chrome shows as "(canceled)" — rejects here
        // even though the backend has usually already created the account and
        // sent the invitation (MIST-5468). Surfacing "Failed to save" in that
        // case is a false negative, so treat an abort as a successful save and
        // let the list refetch reflect the new user.
        if (isAbortError(err)) {
          pendingRedirectRef.current = '/admin/users';
          dispatch(setViewIsDirty(false));
          setError(false);
        } else {
          toast.error(err.data?.message || 'Failed to save user changes.', {
            position: toast.POSITION.BOTTOM_CENTER,
          });
        }
      }
    } else {
      if (
        tenantRoles.length &&
        tenantRoles.filter((tenant) => tenant.role !== 'Unauthorized').length <
          1
      )
        toast.error('User must be authorized for at least one environment', {
          position: toast.POSITION.BOTTOM_CENTER,
        });
    }
  };

  const languageChangeHandler = (event) => {
    dispatch(setViewIsDirty(true));
    setPreferredLanguage(event.target.value);
    setPreferredLanguageState('success');
  };

  const environmentChangeHandler = (event) => {
    dispatch(setViewIsDirty(true));
    setDefaultEnvironment(event.target.value);
    setDefaultEnvironmentState('success');
  };

  const administratorRoleChangeHandler = (event) => {
    dispatch(setViewIsDirty(true));
    setAdministratorRole(event.target.value);
    setAdministratorRoleState('success');
  };

  const [editEnvironment, setEditEnvironment] = React.useState(false);
  const [currentEnvironment, setCurrentEnvironment] = React.useState(null);
  const [currentIndex, setCurrentIndex] = React.useState(0);

  const handleEditEnvironment = (index) => {
    const currentEnv = tenantRoles[index];
    setCurrentEnvironment({
      ...currentEnv,
      default: currentEnv.tenant_id === defaultEnvironment,
    });
    setCurrentIndex(index);
    setEditEnvironment(true);
  };

  const handleUpdateRole = (tenantRole) => {
    dispatch(setViewIsDirty(true));
    const newTenantRoles = [...tenantRoles];
    newTenantRoles.splice(currentIndex, 1, tenantRole);
    setTenantRoles(newTenantRoles);
    if (tenantRole.default) setDefaultEnvironment(tenantRole.tenant_id);
    verifyDefaultEnvironment();
  };

  const handleCloseEnvironment = () => {
    setEditEnvironment(false);
  };

  const actionButton = (Icon, color, handler, key) => {
    return (
      <Button
        color={color}
        simple
        className={classes.actionButton}
        onClick={() => handler(key)}
      >
        <Icon className={classes.icon} />
      </Button>
    );
  };

  const defaultCheck = (tenant) => {
    return tenant.tenant_id === defaultEnvironment ? (
      <Button color='success' simple className={classes.actionButton}>
        <Check className={classes.icon} />
      </Button>
    ) : (
      ''
    );
  };

  const defaultEnvironmentSelection = () => {
    const authorizedEnvironments = tenantRoles?.filter(
      (tenant) => tenant.role !== 'Unauthorized',
    );
    const environmentList = authorizedEnvironments.map((tenant, key) => {
      const option = (
        <MenuItem key={key} value={tenant.tenant_id}>
          {tenant.label}
        </MenuItem>
      );
      return option;
    });
    if (environmentList.length < 1)
      environmentList.unshift(
        <MenuItem key='' value=''>
          Unavailable
        </MenuItem>,
      );

    return (
      <Select
        variant='standard'
        className={classes.select}
        labelId='environment-select-label'
        id='environment-select'
        value={defaultEnvironment}
        onChange={(event) => environmentChangeHandler(event)}
      >
        {environmentList}
      </Select>
    );
  };

  const preferredLanguageSelection = () => {
    const languageList = supportedLanguages?.map((language, key) => {
      const option = (
        <MenuItem key={key} value={language}>
          {language}
        </MenuItem>
      );
      return option;
    });

    return (
      <Select
        variant='standard'
        className={classes.select}
        labelId='language-select-label'
        id='language-select'
        value={preferredLanguage}
        onChange={(event) => languageChangeHandler(event)}
      >
        {languageList}
      </Select>
    );
  };

  const administratorRoleSelection = () => {
    const roleList = ntRoles.map((role, key) => {
      const option = (
        <MenuItem key={key} value={role.name}>
          {role.name}
        </MenuItem>
      );
      return option;
    });

    return (
      <Select
        variant='standard'
        className={classes.select}
        labelId='admin-role-select-label'
        id='admin-role-select'
        value={administratorRole}
        onChange={(event) => administratorRoleChangeHandler(event)}
      >
        {roleList}
      </Select>
    );
  };

  const environments = () => {
    // start with the full list of tenants for the client
    const environmentList = tenantRoles.map((tenant, key) => {
      var clientTenant = [];
      var actionButtons = [];
      clientTenant.push(tenant.label);
      clientTenant.push(tenant.role);
      clientTenant.push(defaultCheck(tenant));
      actionButtons.push(
        actionButton(Edit, 'warning', handleEditEnvironment, key),
      );
      clientTenant.push(actionButtons);
      return clientTenant;
    });
    return environmentList;
  };

  useEffect(() => {
    dispatch(setViewIsDirty(false));
  }, []);

  useEffect(() => {
    if (pendingRedirectRef.current) {
      const target = pendingRedirectRef.current;
      pendingRedirectRef.current = null;
      props.history.push(target);
    }
  }, [viewIsDirty]);

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='rose' icon>
            <CardIcon color='rose'>
              <UserIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>User Information</h4>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={8}>
                {client?.name && <h4>User for {client.name}</h4>}
                <CustomInput
                  helperText={
                    fullNameState === 'error'
                      ? fullNameError ?? 'An error has occurred'
                      : false
                  }
                  success={fullNameState === 'success'}
                  error={fullNameState === 'error'}
                  labelText='Full Name'
                  id='name'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: fullName,
                    onChange: (event) => {
                      if (verifyLength(event.target.value, fullName, 3)) {
                        setFullNameState('success');
                        setFullNameError('');
                      } else {
                        setFullNameState('error');
                        setFullNameError(
                          'The field should be at least 3 characters long',
                        );
                      }
                      setFullName(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <CustomInput
                  helperText={
                    displayNameState === 'error'
                      ? displayNameError ?? 'An error has occurred'
                      : false
                  }
                  success={displayNameState === 'success'}
                  error={displayNameState === 'error'}
                  labelText='Display Name'
                  id='display'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: displayName,
                    onChange: (event) => {
                      if (verifyLength(event.target.value, displayName, 3)) {
                        setDisplayNameState('success');
                        setDisplayNameError('');
                      } else {
                        setDisplayNameState('error');
                        setDisplayNameError(
                          'The field should be at least 3 characters long',
                        );
                      }
                      setDisplayName(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <CustomInput
                  helperText={
                    emailAddressState === 'error'
                      ? emailAddressError ?? 'An error has occurred'
                      : false
                  }
                  success={emailAddressState === 'success'}
                  error={emailAddressState === 'error'}
                  labelText='Email Address'
                  id='email'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    value: emailAddress,
                    inputComponent: emailMaskCustom,
                    onChange: (event) => {
                      if (verifyEmail(event.target.value, emailAddress)) {
                        setEmailAddressState('success');
                      } else {
                        setEmailAddressState('error');
                      }
                      setEmailAddress(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <CustomInput
                  labelText='Phone Number'
                  id='phone'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    value: phoneNumber,
                    onChange: (event) => {
                      if (event.target.value !== phoneNumber) {
                        dispatch(setViewIsDirty(true));
                      }
                      setPhoneNumber(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
              </GridItem>
              <GridItem xs={4}>
                <GridItem xs={12}>
                  <FormControl
                    variant='standard'
                    className={classes.formControl}
                  >
                    <InputLabel
                      id='language-select-label'
                      className={classes.inputLabel}
                    >
                      Preferred Language
                    </InputLabel>
                    {preferredLanguageSelection()}
                  </FormControl>
                </GridItem>
                <GridItem xs={12}>
                  {!user.nt_administrator ? (
                    <FormControl
                      variant='standard'
                      className={classes.formControl}
                      success={defaultEnvironmentState === 'success'}
                      error={defaultEnvironmentState === 'error'}
                    >
                      <InputLabel
                        id='environment-select-label'
                        className={classes.inputLabel}
                      >
                        Default Environment
                      </InputLabel>
                      {defaultEnvironmentSelection()}
                      {defaultEnvironmentState === 'error' ? (
                        <FormHelperText>The field is required.</FormHelperText>
                      ) : null}
                    </FormControl>
                  ) : (
                    <FormControl
                      variant='standard'
                      className={classes.formControl}
                      success={administratorRoleState === 'success'}
                      error={administratorRoleState === 'error'}
                    >
                      <InputLabel
                        id='role-select-label'
                        className={classes.inputLabel}
                      >
                        Administrator Role
                      </InputLabel>
                      {administratorRoleSelection()}
                    </FormControl>
                  )}
                </GridItem>
              </GridItem>
              <GridItem xs={12}>
                <Button
                  color='rose'
                  onClick={changeClick}
                  className={classes.registerButton}
                  disabled={!viewIsDirty || error}
                >
                  Save Changes
                </Button>
                {!newUser && user.status !== 'active' ? (
                  <Button
                    color='rose'
                    onClick={reinviteUser}
                    className={classes.registerButton}
                  >
                    Invite User
                  </Button>
                ) : (
                  ''
                )}
                {!newUser &&
                user.status !== 'inactive' &&
                user.status !== 'deleted' ? (
                  <Button
                    color='rose'
                    onClick={deactivateUser}
                    className={classes.registerButton}
                  >
                    Deactivate User
                  </Button>
                ) : (
                  ''
                )}
                <Button
                  color='secondary'
                  onClick={() => props.history.push('/admin/users')}
                  className={classes.registerButton}
                >
                  Cancel
                </Button>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </GridItem>
      {!user.nt_administrator ? (
        <GridItem xs={12}>
          <Card>
            <CardHeader color='rose' icon>
              <CardIcon color='rose'>
                <Environment />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Environments</h4>
            </CardHeader>
            <CardBody>
              <Table
                tableHead={['Environment', 'Role', 'Default', 'Actions']}
                tableData={environments()}
              />
            </CardBody>
          </Card>
        </GridItem>
      ) : (
        ''
      )}

      {editEnvironment === true ? (
        <RoleSelector
          open={editEnvironment}
          handleClose={handleCloseEnvironment}
          handleUpdate={handleUpdateRole}
          roleScope={user.nt_administrator ? 'NT' : 'Client'}
          tenantRole={currentEnvironment}
        />
      ) : null}
    </GridContainer>
  );
}
